import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IconChevronLeft, IconLeaf, IconPillFilled } from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { MCheckbox } from "@/components/ui/atoms/MCheckbox";
import { documentService } from "@/services/documents.service";
import { getMemberInfo } from "@/hooks/useMembers";
import { NAVIGATE_BACK_IN_HISTORY } from "@/hooks/useBackNavigation";
import { MedicineItemType, MedicineTimingRuleType } from "@/types/pillbox";
import {
  DAY_FREQUENCY_LABELS,
  formatDisplayDate,
  formatDoctorDateBadge,
} from "./utils/formatMedicine";
import {
  mapPendingChangeToDraftItem,
  PendingGroup,
} from "./utils/pendingChange";
import styles from "./ChoosePrescriptionVersion.module.css";

const ChoosePrescriptionVersion: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;

  const group = location.state?.group as PendingGroup | undefined;
  const items = useMemo(() => group?.items ?? [], [group]);
  const [selectedId, setSelectedId] = useState<string | undefined>(
    items[0]?.id
  );
  const [openingId, setOpeningId] = useState<string | null>(null);

  if (!group || items.length === 0) {
    return null;
  }

  const isSupplement = group.itemType === MedicineItemType.SUPPLEMENT;

  const handleViewPrescription = async (documentId: string) => {
    if (openingId) return;
    setOpeningId(documentId);
    try {
      const document = await documentService(memberId).getById(documentId);
      navigate("/documents/view", {
        state: {
          document,
          // This screen's own history entry already holds location.state
          // (the prescription group) — pop back to it via navigate(-1)
          // instead of pushing a reconstructed copy, which grew the history
          // stack on every Back tap and produced a Back/Back navigation loop.
          backUrl: NAVIGATE_BACK_IN_HISTORY,
        },
      });
    } finally {
      setOpeningId(null);
    }
  };

  const handleContinue = () => {
    const chosen = items.find((i) => i.id === selectedId);
    if (!chosen) return;
    const siblingPendingIds = items
      .filter((i) => i.id !== chosen.id)
      .map((i) => i.id);
    navigate("/pillbox/add-medicine", {
      state: {
        mode: "review",
        item: mapPendingChangeToDraftItem(chosen),
        pendingId: chosen.id,
        siblingPendingIds,
      },
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
        </div>

        <div className={styles.textHeader}>
          <h1 className={styles.title}>
            {t("pillbox.review.chooseTitle", { member: memberDisplayName })}
          </h1>
          <p className={styles.subtitle}>
            {t("pillbox.review.chooseSubtitle", {
              member: memberDisplayName,
            })}
          </p>

          <div className={styles.previewCard}>
            <span className={styles.previewIcon}>
              {isSupplement ? (
                <IconLeaf size={24} stroke={1.5} />
              ) : (
                <IconPillFilled size={24} />
              )}
            </span>
            <span className={styles.previewName}>{group.name}</span>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <span className={styles.groupBadge}>
          {t("pillbox.review.inPrescriptions", { count: items.length })}
        </span>

        {items.map((pending) => {
          const draft = mapPendingChangeToDraftItem(pending);
          const isConditional =
            draft.timingRuleType === MedicineTimingRuleType.CONDITIONAL;
          const cadence = isConditional
            ? t("pillbox.review.asNeeded")
            : !draft.recurrenceIntervalDays || draft.recurrenceIntervalDays <= 1
            ? t("pillbox.review.everyDay")
            : t("pillbox.review.everyNDays", {
                count: draft.recurrenceIntervalDays,
              });
          const highlight = isConditional
            ? t("pillbox.sos")
            : draft.dayFrequency
            ? DAY_FREQUENCY_LABELS[draft.dayFrequency]
            : "";

          return (
            <div className={styles.card} key={pending.id}>
              <div className={styles.cardMain}>
                <div className={styles.cardText}>
                  <span className={styles.prescriptionLabel}>
                    {[
                      t("pillbox.review.prescriptionWord"),
                      // Empty when neither doctor nor visit date is known;
                      // filtered out so no dangling " • " is left behind.
                      formatDoctorDateBadge(
                        pending.doctorName,
                        pending.dateOfVisit
                          ? formatDisplayDate(pending.dateOfVisit)
                          : undefined
                      ),
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </span>
                  <p className={styles.frequency}>
                    {cadence}
                    {highlight && (
                      <>
                        {" • "}
                        <span className={styles.highlight}>{highlight}</span>
                      </>
                    )}
                  </p>
                  <button
                    type="button"
                    className={styles.viewLink}
                    onClick={() =>
                      handleViewPrescription(pending.sourceDocumentId)
                    }
                    disabled={openingId === pending.sourceDocumentId}
                  >
                    {t("pillbox.review.viewPrescription")}
                  </button>
                </div>
                <MCheckbox
                  checked={selectedId === pending.id}
                  onChange={() => setSelectedId(pending.id)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <MButton
          variant="filled"
          size="lg"
          text={t("pillbox.review.continue")}
          onClick={handleContinue}
          disabled={!selectedId}
          className={styles.continueButton}
        />
      </div>
    </div>
  );
};

export default ChoosePrescriptionVersion;
